import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './Toast.css';

/**
 * 🍞 Enhanced Toast Notification System
 * 
 * Features:
 * ✅ Multiple toast types (success, error, warning, info)
 * ✅ Auto-dismiss with customizable duration
 * ✅ Manual dismiss
 * ✅ Stacking with animation
 * ✅ Accessible with ARIA labels
 * ✅ Responsive design
 * ✅ Dark mode support
 * ✅ Progress bar
 * ✅ Action buttons
 * ✅ Rich content support
 */

// Toast Manager - Global state
class ToastManager {
  constructor() {
    this.toasts = [];
    this.listeners = [];
    this.idCounter = 0;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    this.listeners.forEach(listener => listener(this.toasts));
  }

  add(toast) {
    const id = ++this.idCounter;
    const newToast = {
      id,
      timestamp: Date.now(),
      ...toast,
    };
    
    this.toasts.unshift(newToast);
    this.notify();
    
    // Auto dismiss
    if (toast.duration !== 0) {
      setTimeout(() => {
        this.remove(id);
      }, toast.duration || 5000);
    }
    
    return id;
  }

  remove(id) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notify();
  }

  clear() {
    this.toasts = [];
    this.notify();
  }
}

const toastManager = new ToastManager();

// Individual Toast Component
const Toast = ({ 
  toast, 
  onRemove,
  position = 'top-right' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [progress, setProgress] = useState(100);

  // Show toast with animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (toast.duration === 0) return;

    const startTime = Date.now();
    const duration = toast.duration || 5000;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining > 0) {
        requestAnimationFrame(updateProgress);
      }
    };

    requestAnimationFrame(updateProgress);
  }, [toast.duration]);

  // Remove with animation
  const handleRemove = useCallback(() => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 250);
  }, [toast.id, onRemove]);

  // Handle action click
  const handleActionClick = (action) => {
    if (action.onClick) {
      action.onClick();
    }
    if (action.dismissOnClick !== false) {
      handleRemove();
    }
  };

  // Get icon based on type
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'info':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`
        toast 
        toast-${toast.type || 'info'} 
        toast-${position}
        ${isVisible ? 'toast-visible' : ''} 
        ${isRemoving ? 'toast-removing' : ''}
      `}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Progress bar */}
      {toast.showProgress !== false && toast.duration !== 0 && (
        <div 
          className="toast-progress" 
          style={{ width: `${progress}%` }}
        />
      )}

      {/* Content */}
      <div className="toast-content">
        {/* Icon */}
        {toast.showIcon !== false && (
          <div className="toast-icon">
            {toast.icon || getIcon()}
          </div>
        )}

        {/* Message */}
        <div className="toast-message">
          {toast.title && (
            <div className="toast-title">{toast.title}</div>
          )}
          <div className="toast-description">
            {toast.message || toast.description}
          </div>
        </div>

        {/* Actions */}
        {toast.actions && toast.actions.length > 0 && (
          <div className="toast-actions">
            {toast.actions.map((action, index) => (
              <button
                key={index}
                type="button"
                className={`toast-action ${action.style || 'secondary'}`}
                onClick={() => handleActionClick(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Close button */}
        {toast.dismissible !== false && (
          <button
            type="button"
            className="toast-close"
            onClick={handleRemove}
            aria-label="Close notification"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

Toast.propTypes = {
  toast: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    message: PropTypes.string,
    description: PropTypes.string,
    title: PropTypes.string,
    duration: PropTypes.number,
    dismissible: PropTypes.bool,
    showIcon: PropTypes.bool,
    showProgress: PropTypes.bool,
    icon: PropTypes.node,
    actions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      style: PropTypes.oneOf(['primary', 'secondary', 'danger']),
      dismissOnClick: PropTypes.bool,
    })),
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  position: PropTypes.string,
};

// Toast Container Component
const ToastContainer = ({ 
  position = 'top-right',
  maxToasts = 5,
  className = '' 
}) => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    return toastManager.subscribe(setToasts);
  }, []);

  const handleRemove = useCallback((id) => {
    toastManager.remove(id);
  }, []);

  // Limit number of visible toasts
  const visibleToasts = toasts.slice(0, maxToasts);

  if (visibleToasts.length === 0) return null;

  return createPortal(
    <div 
      className={`toast-container toast-container-${position} ${className}`}
      aria-live="polite"
      aria-label="Notifications"
    >
      {visibleToasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onRemove={handleRemove}
          position={position}
        />
      ))}
    </div>,
    document.body
  );
};

ToastContainer.propTypes = {
  position: PropTypes.oneOf([
    'top-left', 'top-center', 'top-right',
    'bottom-left', 'bottom-center', 'bottom-right'
  ]),
  maxToasts: PropTypes.number,
  className: PropTypes.string,
};

// Toast API functions
const toast = {
  success: (message, options = {}) => 
    toastManager.add({ ...options, type: 'success', message }),
  
  error: (message, options = {}) => 
    toastManager.add({ ...options, type: 'error', message }),
  
  warning: (message, options = {}) => 
    toastManager.add({ ...options, type: 'warning', message }),
  
  info: (message, options = {}) => 
    toastManager.add({ ...options, type: 'info', message }),
  
  custom: (options) => 
    toastManager.add(options),
  
  dismiss: (id) => 
    toastManager.remove(id),
  
  clear: () => 
    toastManager.clear(),
};

export { toast, ToastContainer };
export default toast;